import React, { useState, useEffect, useCallback } from "react";
import { FaArrowLeft, FaArrowRight, FaStar, FaQuoteLeft } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "@/axios";
import person from "@/assets/images/background.jpg";

const TestimonialCarousel = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [direction, setDirection] = useState(1);

    useEffect(() => {
        fetchTestimonials();
    }, []);

    const fetchTestimonials = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get("/testimonials");
            setTestimonials(response?.data?.data || []);
        } catch (error) {
            console.error("Failed to fetch testimonials:", error);
        } finally {
            setLoading(false);
        }
    };

    const next = useCallback(() => {
        setDirection(1);
        setCurrentIndex((i) => (i + 1) % testimonials.length);
    }, [testimonials.length]);

    const prev = useCallback(() => {
        setDirection(-1);
        setCurrentIndex((i) => (i - 1 + testimonials.length) % testimonials.length);
    }, [testimonials.length]);

    const current = testimonials[currentIndex];

    const variants = {
        enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit:  (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
    };

    return (
        <section className="w-full py-10 px-4">
            {/* Section Header */}
            <div className="text-center mb-14 space-y-3">
                <span
                    className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full"
                    style={{ background: "#eff6ff", color: "#264b82", border: "1px solid #bfdbfe" }}
                >
                    Testimonials
                </span>
                <h2 className="text-4xl lg:text-5xl font-black tracking-tight" style={{ color: "#1a3560" }}>
                    What Experts Say
                </h2>
                <p className="text-base max-w-md mx-auto" style={{ color: "#64748b" }}>
                    Hear directly from industry professionals who have shaped their careers through our platform.
                </p>
            </div>

            {/* Card Area */}
            <div className="relative max-w-3xl mx-auto">

                {loading && testimonials.length === 0 && (
                    <div className="flex justify-center items-center py-20">
                        <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: "#264b82", borderTopColor: "transparent" }} />
                    </div>
                )}

                {!loading && testimonials.length === 0 && (
                    <p className="text-center py-16" style={{ color: "#94a3b8" }}>No testimonials available.</p>
                )}

                {testimonials.length > 0 && (
                    <>
                        <div className="relative bg-white rounded-2xl shadow-xl border overflow-hidden" style={{ borderColor: "#e2e8f0" }}>

                            {/* Top accent bar */}
                            <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, #264b82, #3b82f6)" }} />

                            <div className="p-8 md:p-10">
                                <AnimatePresence mode="wait" custom={direction}>
                                    <motion.div
                                        key={currentIndex}
                                        custom={direction}
                                        variants={variants}
                                        initial="enter"
                                        animate="center"
                                        exit="exit"
                                        transition={{ duration: 0.35, ease: "easeInOut" }}
                                    >
                                        {/* Quote icon */}
                                        <FaQuoteLeft className="mb-6 text-3xl" style={{ color: "#bfdbfe" }} />

                                        {/* Testimonial text */}
                                        <p className="text-lg leading-relaxed mb-8 italic" style={{ color: "#334155" }}>
                                            "{current?.testimonial}"
                                        </p>

                                        {/* Divider */}
                                        <div className="border-t mb-6" style={{ borderColor: "#f1f5f9" }} />

                                        {/* Author row */}
                                        <div className="flex items-center justify-between flex-wrap gap-4">
                                            <div className="flex items-center gap-4">
                                                <img
                                                    src={current?.photo || person}
                                                    alt={current?.name}
                                                    className="w-14 h-14 rounded-full object-cover shadow-md flex-shrink-0"
                                                    style={{ border: "2px solid #e0e7ff" }}
                                                    onError={(e) => { e.target.onerror = null; e.target.src = person; }}
                                                />
                                                <div>
                                                    <p className="font-bold text-sm" style={{ color: "#1e293b" }}>
                                                        {current?.name}
                                                    </p>
                                                    <p className="text-xs font-medium" style={{ color: "#264b82" }}>
                                                        {current?.designation}
                                                    </p>
                                                    {current?.company && (
                                                        <p className="text-xs" style={{ color: "#94a3b8" }}>
                                                            {current?.company}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Stars */}
                                            <div className="flex items-center gap-1">
                                                {Array.from({ length: 5 }).map((_, i) => (
                                                    <FaStar key={i} className="text-sm" style={{ color: "#fbbf24" }} />
                                                ))}
                                            </div>
                                        </div>
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>

                        {/* Navigation */}
                        <div className="flex items-center justify-center gap-6 mt-8">
                            <button
                                onClick={prev}
                                className="w-10 h-10 rounded-full flex items-center justify-center shadow-md transition-all duration-200 hover:-translate-y-0.5"
                                style={{ background: "white", border: "1px solid #e2e8f0", color: "#264b82" }}
                                aria-label="Previous"
                            >
                                <FaArrowLeft className="text-sm" />
                            </button>

                            {/* Dot indicators */}
                            <div className="flex items-center gap-2">
                                {testimonials.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => { setDirection(i > currentIndex ? 1 : -1); setCurrentIndex(i); }}
                                        className="rounded-full transition-all duration-300"
                                        style={{
                                            width: i === currentIndex ? "24px" : "8px",
                                            height: "8px",
                                            background: i === currentIndex ? "#264b82" : "#cbd5e1",
                                        }}
                                        aria-label={`Go to testimonial ${i + 1}`}
                                    />
                                ))}
                            </div>

                            <button
                                onClick={next}
                                className="w-10 h-10 rounded-full flex items-center justify-center shadow-md transition-all duration-200 hover:-translate-y-0.5"
                                style={{ background: "#264b82", color: "white" }}
                                aria-label="Next"
                            >
                                <FaArrowRight className="text-sm" />
                            </button>
                        </div>

                        {/* Counter */}
                        <p className="text-center mt-4 text-xs" style={{ color: "#94a3b8" }}>
                            {currentIndex + 1} / {testimonials.length}
                        </p>
                    </>
                )}
            </div>
        </section>
    );
};

export default TestimonialCarousel;
